/**
 * Hidden attribution input injection. On form discovery we add an empty
 * `<input type="hidden" name="attribution_*">` per field. The capture-phase
 * submit handler populates values immediately before Webflow's native
 * submit reads the FormData.
 */

import { capLength, LENGTH_CAPS } from './attribution';
import type { AttributionSnapshot } from './attribution';
import { posthogSessionId } from './posthog';

export const ATTRIBUTION_FIELD_NAMES = [
  'attribution_session_id',
  'attribution_landing_url',
  'attribution_event_id',
  'attribution_form_id',
  'attribution_timestamp',
  'attribution_user_agent',
  'attribution_screen_resolution',
  'attribution_viewport_size',
  'attribution_gbraid',
  'attribution_wbraid',
  'attribution_msclkid',
  'attribution_bot_suspected',
  'attribution_utm_source',
  'attribution_utm_medium',
  'attribution_utm_campaign',
  'attribution_utm_term',
  'attribution_utm_content',
  'attribution_gclid',
  'attribution_fbc',
  'attribution_fbp',
  'attribution_page_url',
  'attribution_referrer',
  'attribution_initial_referrer',
  'attribution_initial_search_engine',
  'attribution_initial_search_query',
  'attribution_posthog_session_id',
  'attribution_campaign_id',
  'attribution_medium_id',
  'attribution_content_id',
  'attribution_ad_id',
] as const;

export function injectHiddenInputs(form: HTMLFormElement): void {
  try {
    for (const name of ATTRIBUTION_FIELD_NAMES) {
      let existing = form.querySelector<HTMLInputElement>(
        'input[name="' + name + '"][data-ign-pixel="1"]'
      );
      if (existing) continue;
      // Don't collide with a developer-placed hidden input with the same name.
      existing = form.querySelector<HTMLInputElement>('input[name="' + name + '"]');
      if (existing) continue;
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.setAttribute('data-ign-pixel', '1');
      form.appendChild(input);
    }
  } catch {
    /* injection best-effort */
  }
}

export interface AttributionFieldValues {
  attribution: AttributionSnapshot;
  eventId: string;
  formId: string;
  timestampIso: string;
  botSuspected: boolean;
}

export function populateHiddenInputs(form: HTMLFormElement, values: AttributionFieldValues): void {
  try {
    const { attribution, eventId, formId, timestampIso, botSuspected } = values;
    const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '';
    const screenRes = typeof screen !== 'undefined' ? screen.width + 'x' + screen.height : '';
    const viewport =
      typeof window !== 'undefined' ? window.innerWidth + 'x' + window.innerHeight : '';
    const fieldValues: Record<string, string | undefined> = {
      attribution_session_id: attribution.sessionId,
      attribution_landing_url: attribution.landingUrl,
      attribution_event_id: eventId,
      attribution_form_id: formId,
      attribution_timestamp: timestampIso,
      attribution_user_agent: userAgent,
      attribution_screen_resolution: screenRes,
      attribution_viewport_size: viewport,
      attribution_gbraid: attribution.gbraid,
      attribution_wbraid: attribution.wbraid,
      attribution_msclkid: attribution.msclkid,
      attribution_bot_suspected: botSuspected ? 'true' : 'false',
      attribution_utm_source: attribution.utm.utm_source,
      attribution_utm_medium: attribution.utm.utm_medium,
      attribution_utm_campaign: attribution.utm.utm_campaign,
      attribution_utm_term: attribution.utm.utm_term,
      attribution_utm_content: attribution.utm.utm_content,
      attribution_gclid: attribution.gclid,
      attribution_fbc: attribution.fbc,
      attribution_fbp: attribution.fbp,
      attribution_page_url: attribution.pageUrl,
      attribution_referrer: attribution.referrer,
      attribution_initial_referrer: attribution.initialReferrer,
      attribution_initial_search_engine: attribution.initialSearchEngine,
      attribution_initial_search_query: attribution.initialSearchQuery,
      // window.posthog.get_session_id() can be overridden by extensions,
      // test stubs, or malicious scripts; cap before injecting so an
      // attacker-controlled posthog stub can't bloat the form submission
      // or push past the backend's CHECK on posthog_session_id (migration 112).
      attribution_posthog_session_id: capLength(posthogSessionId(), LENGTH_CAPS.posthogSessionId),
      attribution_campaign_id: attribution.adIds?.campaign_id,
      attribution_medium_id: attribution.adIds?.medium_id,
      attribution_content_id: attribution.adIds?.content_id,
      attribution_ad_id: attribution.adIds?.ad_id,
    };
    for (const name of ATTRIBUTION_FIELD_NAMES) {
      const input = form.querySelector<HTMLInputElement>('input[name="' + name + '"]');
      if (!input) continue;
      const value = fieldValues[name];
      input.value = value === undefined ? '' : value;
    }
  } catch {
    /* population best-effort */
  }
}

const OBSERVE_DEBOUNCE_MS = 100;

/**
 * Observe the document for forms matching `selector` and inject hidden
 * inputs the moment they appear (covers Webflow re-rendering and SPAs).
 *
 * The MutationObserver callback is debounced to OBSERVE_DEBOUNCE_MS so a
 * chatty page (SPA navigation, live chat widget streaming DOM nodes) does
 * not turn this into a hot querySelectorAll loop. Injections are
 * idempotent (each input is keyed by data-ign-pixel="1" + name), so
 * coalescing repeated callbacks loses nothing.
 */
export function observeAndInject(selector: string): MutationObserver | undefined {
  if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') return undefined;
  const apply = (): void => {
    try {
      const forms = document.querySelectorAll<HTMLFormElement>(selector);
      forms.forEach((f) => injectHiddenInputs(f));
    } catch {
      /* selector invalid */
    }
  };
  apply();
  let timer: ReturnType<typeof setTimeout> | null = null;
  const schedule = (): void => {
    if (timer !== null) return;
    timer = setTimeout(() => {
      timer = null;
      apply();
    }, OBSERVE_DEBOUNCE_MS);
  };
  const observer = new MutationObserver(schedule);
  try {
    observer.observe(document.documentElement, { childList: true, subtree: true });
  } catch {
    /* observe failed */
  }
  return observer;
}
